import { useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

import { LoginProvider } from '@repo/types';
import { authApi } from '@xtnd/web/features/auth/api';
import { useDarkMode } from '@xtnd/web/shared/hooks/useDarkMode';
import { Button, Card, CardHeader, CardBody, Icon, XTNDLogo, XTNDLogoDark } from '@repo/ui';
import { useSocialLogin, useOAuthCallback } from '@xtnd/web/features/auth/queries/useAuthQuery';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isDark } = useDarkMode();

  // Get the intended destination from location state
  const from = location.state?.from?.pathname || '/';

  // Check if already authenticated and redirect
  useEffect(() => {
    if (authApi.isAuthenticated()) {
      // Only redirect to 'from' if it's not the current login page and user didn't just log out
      const shouldRedirectToFrom =
        from !== '/login' && from !== '/' && !location.search.includes('logout');
      const destination = shouldRedirectToFrom ? from : '/juniper-transactions';
      navigate(destination, { replace: true });
    }
  }, [navigate, from, location.search]);

  // Initialize hooks
  const socialLoginMutation = useSocialLogin();
  const oauthCallbackMutation = useOAuthCallback();

  // Clear logout parameter from URL after logout to clean up state
  useEffect(() => {
    if (searchParams.get('logout') === 'true') {
      // Clear the logout parameter after a brief delay to clean up URL
      const timer = setTimeout(() => {
        navigate('/login', { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [searchParams, navigate]);

  // Intentionally do not handle OAuth callback here; use dedicated /auth/callback route

  const handleSocialLogin = (provider: LoginProvider) => {
    socialLoginMutation.mutate({ provider });
  };

  // Determine loading state
  const isLoading = socialLoginMutation.isPending || oauthCallbackMutation.isPending;

  return (
    <div className="min-h-screen bg-gradient-to-br from-xtnd-blue via-xtnd-blue to-xtnd-dark flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-xtnd-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-xtnd-light rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 w-full max-w-lg">
        {/* Header Section */}
        <div className="text-center mb-8">
          {/* Logo Container - force light logo in both themes */}
          <div className="flex justify-center mb-6">
            <div className="bg-xtnd-white dark:bg-xtnd-dark rounded-2xl p-6 shadow-2xl border border-xtnd-dark-100 dark:border-xtnd-dark-200">
              {isDark ? <XTNDLogoDark size="2xl" /> : <XTNDLogo size="2xl" />}
            </div>
          </div>

          {/* Brand Title */}
          <div className="space-y-2">
            <h1 className="text-4xl font-bold text-xtnd-dark dark:text-xtnd-white drop-shadow-2xl tracking-tight">
              XTND Juniper
            </h1>
            <p className="text-xtnd-dark/80 dark:text-xtnd-white/80 text-lg font-medium drop-shadow-xl">
              Legal Invoice Automation Platform
            </p>
          </div>
        </div>

        {/* Login Card */}
        <Card
          variant="default"
          className="shadow-2xl border-0 bg-xtnd-white/95 backdrop-blur-sm dark:bg-xtnd-dark-800 dark:border-xtnd-dark-600"
        >
          <CardHeader className="text-center pb-8 pt-8">
            <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-2">
              Welcome Back
            </h2>
            <p className="text-xtnd-dark/70 dark:text-xtnd-white/70 text-base">
              Sign in to access your legal automation dashboard
            </p>
          </CardHeader>

          <CardBody className="px-8 pb-8">
            <div className="space-y-6">
              {/* Social Login Buttons */}
              <div className="space-y-4">
                <Button
                  type="button"
                  variant="social"
                  size="lg"
                  onClick={() => handleSocialLogin(LoginProvider.Clio)}
                  disabled={isLoading}
                  fullWidth
                  className="justify-start pl-5"
                  leftIcon={<Icon name="ClioIcon" />}
                >
                  <span className="ml-2">Continue with Clio</span>
                </Button>

                <Button
                  type="button"
                  variant="social"
                  size="lg"
                  onClick={() => handleSocialLogin(LoginProvider.GOOGLE)}
                  disabled={isLoading}
                  fullWidth
                  className="justify-start pl-5"
                  leftIcon={<Icon name="GoogleIcon" />}
                >
                  <span className="ml-2">Continue with Google</span>
                </Button>

                <Button
                  type="button"
                  variant="social"
                  size="lg"
                  onClick={() => handleSocialLogin(LoginProvider.MICROSOFT)}
                  disabled={isLoading}
                  fullWidth
                  className="justify-start pl-5"
                  leftIcon={<Icon name="MicrosoftIcon" />}
                >
                  <span className="ml-2">Continue with Microsoft</span>
                </Button>
              </div>

              {/* Loading State */}
              {isLoading && (
                <div className="flex items-center justify-center py-6 border-t border-xtnd-dark-50 dark:border-xtnd-dark-700">
                  <div className="flex items-center text-xtnd-blue">
                    <Icon name="LoadingSpinner" className="-ml-1 mr-3" />
                    <span className="text-base font-medium">Redirecting to login provider...</span>
                  </div>
                </div>
              )}

              {/* Security Notice */}
              <div className="pt-4 border-t border-xtnd-dark-50 dark:border-xtnd-dark-100">
                <div className="flex items-center justify-center text-center">
                  <div className="flex items-center text-sm text-xtnd-dark/60 dark:text-xtnd-white/60">
                    <Icon
                      name="SecurityIcon"
                      size="sm"
                      className="mr-2 text-xtnd-blue dark:text-xtnd-white"
                    />
                    Secure enterprise-grade authentication
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 space-y-3">
          <p className="text-xtnd-light/80 text-sm">Need help accessing your account?</p>
          <p className="text-xtnd-light/60 text-sm">
            Contact your system administrator for assistance
          </p>
        </div>
      </div>
    </div>
  );
}
